import actionFilterModule from '@/store/action-filter'

const { ANALYTIC_EVENT_TYPES, ANALYTIC_EVENT_TYPES_V2, ANALYTIC_EVENTS_V2 } =
  actionFilterModule.state

const INCLUDE = 'include'
const EXCLUDE = 'exclude'

const ALL_COMPANIES = 'ALL_COMPANIES'
const OBJECT_ID = /^[a-f\d]{24}$/i

const DATE_UNITS = ['days', 'weeks', 'months']
const DATE_TYPE_AGO = 'ago'
const DATE_TYPE_RANGE = 'range'
const DATE_TYPES_WITH_DATES = [DATE_TYPE_RANGE, 'since', 'on']
const RANGE_SEPARATOR = ' to '

const RATING_MIN = 1
const RATING_MAX = 5

// The endpoint rejects a longer term rather than truncating it, and the page sends on every
// debounced keystroke, so a paste past the bound would break the table instead of narrowing it.
const SEARCH_MAX_LENGTH = 128

// The five the endpoint's enum accepts. ANALYTIC_EVENT_TYPES_V2 also carries `choose-action`,
// which is the not-yet-chosen sentinel rather than a filter and is a 400 if it is sent.
const SUPPORTED_EVENT_TYPES = ANALYTIC_EVENTS_V2.map((event) => event.type)

const V2_BY_V1_EVENT_TYPE = Object.entries(ANALYTIC_EVENT_TYPES).reduce((map, [key, v1]) => {
  const v2 = ANALYTIC_EVENT_TYPES_V2[key]
  if (v2 && SUPPORTED_EVENT_TYPES.includes(v2)) map[v1] = v2
  return map
}, {})

/**
 * The worker re-reads each date in the requesting user's timezone and takes the start of that day,
 * so anchoring the instant at midday UTC keeps the calendar date the user picked from sliding to
 * the neighbouring day once it is translated.
 */
const toIsoDay = (value) => {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12)
  ).toISOString()
}

const toIsoDays = (type, date) => {
  const parts = type === DATE_TYPE_RANGE ? String(date || '').split(RANGE_SEPARATOR) : [date]
  if (type === DATE_TYPE_RANGE && parts.length !== 2) return null

  const dates = parts.map(toIsoDay)
  return dates.every(Boolean) ? dates : null
}

const buildDateFilters = (dateSettings) => {
  const { type, unit, date } = dateSettings || {}
  if (!type) return null

  if (type === DATE_TYPE_AGO) {
    const unitQuantity = parseInt(date, 10)
    if (!DATE_UNITS.includes(unit) || !Number.isInteger(unitQuantity)) return null
    return [{ type, behavior: INCLUDE, unit, unitQuantity }]
  }

  if (DATE_TYPES_WITH_DATES.includes(type)) {
    const dates = toIsoDays(type, date)
    return dates ? [{ type, behavior: INCLUDE, dates }] : null
  }

  return null
}

const buildAnalyticEventFilters = (actions) =>
  (actions || []).reduce((filters, action) => {
    const type = V2_BY_V1_EVENT_TYPE[action.eventType]
    if (!type) return filters

    filters.push({
      types: [type],
      behavior: action.did ? INCLUDE : EXCLUDE,
      ...(type === ANALYTIC_EVENT_TYPES_V2.SENT_CAMPAIGN && action.campaignId
        ? { campaigns: [action.campaignId] }
        : {}),
      ...(type === ANALYTIC_EVENT_TYPES_V2.REDEEM_PROMO && action.promoId
        ? { promos: [action.promoId] }
        : {})
    })
    return filters
  }, [])

const buildTagFilters = (tags) => {
  const selected = (tags || []).filter((tag) => OBJECT_ID.test(tag?.id || ''))
  if (!selected.length) return null

  return [
    {
      tags: selected.map((tag) => tag.id),
      tagNames: selected.map((tag) => tag.name),
      behavior: INCLUDE
    }
  ]
}

const buildSurveyFilters = (surveyRatings) => {
  const ratings = (surveyRatings || [])
    .map((rating) => parseInt(rating, 10))
    .filter((rating) => Number.isInteger(rating) && rating >= RATING_MIN && rating <= RATING_MAX)

  return ratings.length ? [{ ratings, behavior: INCLUDE }] : null
}

/**
 * `marketingOptIn` / `notMarketingOptIn` are never an arbitrary id list, they are a one-element
 * array carrying the active company selector, `ALL_COMPANIES` included. Reading them as a flag and
 * scoping to the already-resolved `companies` (rather than filtering the raw array through
 * `OBJECT_ID`) means an opt-in filter under `ALL_COMPANIES` narrows to every relevant company
 * instead of silently dropping, matching how `companyFilters` itself resolves that sentinel.
 */
const buildMarketingOptInFilters = ({ marketingOptIn, notMarketingOptIn }, companies) => {
  if (marketingOptIn?.length) return [{ companies, behavior: INCLUDE }]
  if (notMarketingOptIn?.length) return [{ companies, behavior: EXCLUDE }]
  return null
}

const buildIdFilters = (key, ids) => {
  const valid = (ids || []).filter((id) => OBJECT_ID.test(id))
  return valid.length ? [{ [key]: valid, behavior: INCLUDE }] : null
}

/**
 * Resolves the companies to scope the list to.
 *
 * `activeCompany._id` is the literal `ALL_COMPANIES` when the user is viewing every company, which
 * is not an ObjectId and fails the company filter outright, so it resolves through the caller's
 * relevant companies instead.
 */
const resolveCompanyIds = (activeCompany, relevantCompanies) => {
  const activeId = activeCompany?._id
  if (activeId && activeId !== ALL_COMPANIES) {
    return OBJECT_ID.test(activeId) ? [activeId] : []
  }

  return (relevantCompanies || []).map((company) => company?._id).filter((id) => OBJECT_ID.test(id))
}

const buildSearch = (search) => {
  const term = typeof search === 'string' ? search.trim() : ''
  return term ? term.slice(0, SEARCH_MAX_LENGTH) : null
}

/**
 * Translates the customers page's flat list filters into the request body the async list endpoint
 * takes. The schema is `additionalProperties: false` and every id array carries `minItems: 1`, so a
 * filter with nothing selected is omitted entirely rather than sent as an empty array.
 *
 * @param {object} options - the options
 * @param {object} options.listFilters - the page's flat filter state
 * @param {object} options.activeCompany - the currently selected company
 * @param {Array} [options.relevantCompanies] - the companies to fall back to under ALL_COMPANIES
 * @param {number} options.limit - page size
 * @param {number} options.skip - page offset
 * @returns {object|null} the request body, or null when no company can be resolved to scope it to
 */
export function buildCustomerListRequest({
  listFilters = {},
  activeCompany,
  relevantCompanies,
  limit,
  skip
}) {
  const companies = resolveCompanyIds(activeCompany, relevantCompanies)
  if (!companies.length) return null

  const dateFilters = buildDateFilters(listFilters.dateSettings)
  const locationFilters = buildIdFilters('locations', listFilters.locations)
  const surveyFilters = buildSurveyFilters(listFilters.surveyRatings)
  const marketingOptInFilters = buildMarketingOptInFilters(listFilters, companies)
  const tagFilters = buildTagFilters(listFilters.tags)
  const analyticEventFilters = buildAnalyticEventFilters(listFilters.actions)
  const search = buildSearch(listFilters.search)

  return {
    customFilter: {
      companyFilters: [{ companies, behavior: INCLUDE }],
      ...(locationFilters ? { locationFilters } : {}),
      ...(dateFilters ? { dateFilters } : {}),
      ...(surveyFilters ? { surveyFilters } : {}),
      ...(marketingOptInFilters ? { marketingOptInFilters } : {}),
      ...(tagFilters ? { tagFilters } : {}),
      ...(analyticEventFilters.length ? { analyticEventFilters } : {})
    },
    ...(search ? { search } : {}),
    limit,
    skip
  }
}
